import { Icon, Label, Textarea, cn } from "@sagot/ui";
import { useId, useState } from "react";
import { WORKFLOW_COMMENT_MAX_CHARS, type WorkflowChoice, type WorkflowRatingScale, type WorkflowStepPayload } from "../workflow/contract";
import type { WorkflowCardLabels } from "../workflow/labels";
import { answeredWorkflowRating, workflowRatingValues } from "../workflow/step";
import { primaryActionClass, selectedChipClass, useChatTheme } from "./chat-theme";

const TARGET = "inline-flex size-11 items-center justify-center rounded-lg disabled:cursor-not-allowed";

export interface WorkflowRatingProps {
  card: WorkflowStepPayload;
  labels: WorkflowCardLabels;
  /** The card was answered (live or restored): read-only record of the rating. */
  settled: boolean;
  /** Another turn is in flight: nothing can be answered right now. */
  disabled: boolean;
  describedBy: string | undefined;
  onAnswer: (card: WorkflowStepPayload, choice: WorkflowChoice) => void;
}

/** The rating as words: "4 out of 5", "Thumbs up". */
function ratingSummary(scale: WorkflowRatingScale, value: string, labels: WorkflowCardLabels): string {
  if (scale === "stars") return labels.starsOutOf(Number(value));
  return value === "up" ? labels.thumbsUp : labels.thumbsDown;
}

function filledStarClass(themed: boolean): string {
  return cn("fill-current", themed ? "text-[var(--wg-primary)]" : "text-primary-ink");
}

interface ChoicesProps {
  labels: WorkflowCardLabels;
  describedBy: string | undefined;
  picked: string | null;
  locked: boolean;
  onPick: (value: string) => void;
}

/** Five star buttons; hovering or focusing one previews the stars up to it. */
function StarButtons({ labels, describedBy, picked, locked, onPick }: ChoicesProps) {
  const { themed } = useChatTheme();
  const [preview, setPreview] = useState<number | null>(null);
  const lit = preview ?? Number(picked ?? 0);
  return (
    <div
      role="group"
      aria-label={labels.ratingGroup}
      aria-describedby={describedBy}
      className="mt-2 flex flex-wrap gap-1"
      onMouseLeave={() => {
        setPreview(null);
      }}
    >
      {workflowRatingValues("stars").map((value) => {
        const stars = Number(value);
        return (
          <button
            key={value}
            type="button"
            disabled={locked}
            aria-label={labels.rateStar(stars)}
            aria-pressed={picked === value}
            onClick={() => {
              onPick(value);
            }}
            onMouseEnter={() => {
              setPreview(stars);
            }}
            onFocus={() => {
              setPreview(stars);
            }}
            onBlur={() => {
              setPreview(null);
            }}
            className={cn(TARGET, "enabled:hover:bg-primary-soft")}
          >
            <Icon name="star" className={cn("size-6", stars <= lit ? filledStarClass(themed) : "text-fg-subtle")} />
          </button>
        );
      })}
    </div>
  );
}

/** Thumbs up / down as two 44px buttons named by their label (the chosen one is filled). */
function ThumbButtons({ labels, describedBy, picked, locked, onPick }: ChoicesProps) {
  const { themed } = useChatTheme();
  return (
    <div role="group" aria-label={labels.ratingGroup} aria-describedby={describedBy} className="mt-2 flex gap-2">
      {workflowRatingValues("thumbs").map((value) => {
        const chosen = picked === value;
        return (
          <button
            key={value}
            type="button"
            disabled={locked}
            aria-label={value === "up" ? labels.thumbsUp : labels.thumbsDown}
            aria-pressed={chosen}
            onClick={() => {
              onPick(value);
            }}
            className={cn(TARGET, "border", chosen ? selectedChipClass(themed) : "border-line bg-surface text-fg-muted enabled:hover:bg-primary-soft")}
          >
            <Icon name={value === "up" ? "thumbs-up" : "thumbs-down"} className={cn("size-5", chosen && "fill-current")} />
          </button>
        );
      })}
    </div>
  );
}

interface CommentFormProps {
  labels: WorkflowCardLabels;
  disabled: boolean;
  onSubmit: (comment: string) => void;
}

/** The optional note and Submit, shown once a rating is picked on a card that asks for a comment. */
function CommentForm({ labels, disabled, onSubmit }: CommentFormProps) {
  const { themed } = useChatTheme();
  const id = useId();
  const [comment, setComment] = useState("");
  return (
    <form
      noValidate
      className="mt-2 space-y-2 rounded-lg border border-line bg-surface p-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(comment);
      }}
    >
      <Label htmlFor={`${id}-comment`}>{labels.ratingComment}</Label>
      <Textarea
        id={`${id}-comment`}
        rows={3}
        maxLength={WORKFLOW_COMMENT_MAX_CHARS}
        value={comment}
        disabled={disabled}
        aria-describedby={`${id}-count`}
        onChange={(event) => {
          setComment(event.target.value);
        }}
      />
      <p id={`${id}-count`} className="text-xs text-fg-muted">
        {labels.ratingCommentCount(comment.length, WORKFLOW_COMMENT_MAX_CHARS)}
      </p>
      <button type="submit" disabled={disabled} className={`${primaryActionClass(themed)} min-h-11 w-full`}>
        {labels.ratingSubmit}
      </button>
    </form>
  );
}

/** What was answered: the filled stars or the chosen thumb as shapes, plus the words. */
function AnsweredRating({ scale, value, labels }: { scale: WorkflowRatingScale; value: string; labels: WorkflowCardLabels }) {
  const { themed } = useChatTheme();
  const lit = scale === "stars" ? Number(value) : 0;
  return (
    <div className="mt-2 space-y-1">
      <div aria-hidden="true" className="flex gap-0.5">
        {scale === "stars" ? (
          workflowRatingValues("stars").map((star) => <Icon key={star} name="star" className={cn("size-5", Number(star) <= lit ? filledStarClass(themed) : "text-fg-subtle")} />)
        ) : (
          <Icon name={value === "up" ? "thumbs-up" : "thumbs-down"} className="size-5 fill-current text-fg-muted" />
        )}
      </div>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-fg-muted">
        <Icon name="check-circle" className="size-4 shrink-0 text-success" />
        <span className="min-w-0 break-words">{labels.answeredWith(ratingSummary(scale, value, labels))}</span>
      </p>
    </div>
  );
}

/**
 * The rating card (`ask_rating`): five stars or thumbs up / down. Without `ask_comment` a pick
 * sends at once; with it the pick is only selected (and can be changed) until the visitor presses
 * Submit, optionally with a note. Answered cards are a read-only record of the rating.
 */
export function WorkflowRating({ card, labels, settled, disabled, describedBy, onAnswer }: WorkflowRatingProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const answered = settled ? answeredWorkflowRating(card) : null;
  if (settled) return answered ? <AnsweredRating scale={card.scale} value={answered} labels={labels} /> : null;

  const serverInvalid = card.error === "invalid" && picked === null;
  const status = serverInvalid ? labels.invalidFromServer : picked ? labels.ratingSelected(ratingSummary(card.scale, picked, labels)) : "";
  const choices: ChoicesProps = {
    labels,
    describedBy,
    picked,
    locked: disabled,
    onPick: (value) => {
      if (card.ask_comment) setPicked(value);
      else onAnswer(card, { value });
    },
  };
  return (
    <div>
      {card.scale === "stars" ? <StarButtons {...choices} /> : <ThumbButtons {...choices} />}
      <p aria-live="polite" className={serverInvalid ? "mt-1.5 flex items-center gap-1 text-xs font-semibold text-danger" : "sr-only"}>
        {serverInvalid ? <Icon name="alert-circle" className="size-3.5 shrink-0" /> : null}
        {status}
      </p>
      {card.ask_comment && picked ? (
        <CommentForm
          labels={labels}
          disabled={disabled}
          onSubmit={(comment) => {
            onAnswer(card, { value: picked, comment });
          }}
        />
      ) : null}
    </div>
  );
}
